import React, { useState, useEffect } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaRegHeart, FaEye, FaShoppingCart, FaTag, FaUniversity, FaStar, FaRegStar, FaMoneyBillWave, FaBox, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import './ProductCard.css';

const ProductCard = ({ product, isPreview = false }) => {
  const { currentUser } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const navigate = useNavigate();

  const handleViewProduct = (e) => {
    if (isPreview) {
      e.preventDefault();
      return;
    }
    navigate(`/products/${product.id}`);
  };

  const getPlainTextDescription = (html) => {
    if (!html || typeof html !== 'string') return '';
    try {
      const withoutTags = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');
      return withoutTags.replace(/\s+/g, ' ').trim();
    } catch (e) {
      return html;
    }
  };

  const getFeaturesArray = (features) => {
    if (!features) return [];
    if (Array.isArray(features)) return features.filter(Boolean);
    if (typeof features === 'string') {
      try {
        const parsed = JSON.parse(features);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  // States for UI effects
  const [inWishlist, setInWishlist] = useState(false);
  const [showAddedToCart, setShowAddedToCart] = useState(false);

  useEffect(() => {
    if (currentUser && product) {
      setInWishlist(isInWishlist(product.id));
    }
  }, [currentUser, product, isInWishlist]);



  // Handle add to cart
  const handleAddToCart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    addToCart(product);
    setShowAddedToCart(true);
    setTimeout(() => setShowAddedToCart(false), 2000);
  };

  // Handle wishlist toggle
  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  // Get product image
  const getProductImage = () => {
    if (product.imageUrl && typeof product.imageUrl === 'string' && product.imageUrl.trim() !== '') {
      return product.imageUrl;
    }
    if (product.image && typeof product.image === 'string' && product.image.trim() !== '') {
      return product.image;
    }
    if (Array.isArray(product.images) && product.images.length > 0) {
      // If images are objects with an 'image' property (from backend)
      if (typeof product.images[0] === 'object' && product.images[0].image) {
        return product.images[0].image;
      }
      // If images are strings (legacy/mock)
      const validImage = product.images.find(img => img && typeof img === 'string' && img.trim() !== '');
      if (validImage) return validImage;
    }
    // Fallback to placeholder
    const seed = product.id || Math.random().toString(36).substring(7);
    return `https://picsum.photos/seed/${seed}/400/300`;
  };
  
  // Get condition class and label
  const getConditionInfo = () => {
    const condition = (product.condition || '').toLowerCase();
    let className = 'new-condition';
    let label = 'New';
    
    if (condition.includes('new')) {
      className = 'new-condition';
      label = 'New';
    } else if (condition.includes('like new')) {
      className = 'like-new-condition';
      label = 'Like New';
    } else if (condition.includes('good')) {
      className = 'good-condition';
      label = 'Good';
    } else if (condition.includes('fair')) {
      className = 'fair-condition';
      label = 'Fair';
    } else if (condition.includes('poor')) {
      className = 'poor-condition';
      label = 'Poor';
    }
    
    return { className, label: product.condition || label };
  };

  const { className: conditionClass, label: conditionLabel } = getConditionInfo();
  const productImage = getProductImage();
  
  // Format price
  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price.includes('GH₵') ? price : `GH₵${price}`;
    }
    return `GH₵${parseFloat(price || 0).toFixed(2)}`;
  };

  const getRatingInfo = () => {
    const rawRating =
      product?.avg_rating ??
      product?.average_rating ??
      product?.rating ??
      product?.avgRating ??
      product?.averageRating;

    const rawCount =
      product?.rating_count ??
      product?.ratings_count ??
      product?.review_count ??
      product?.reviews_count ??
      product?.num_reviews ??
      product?.numReviews;

    const ratingNum = typeof rawRating === 'string' ? parseFloat(rawRating) : rawRating;
    const countNum = typeof rawCount === 'string' ? parseInt(rawCount, 10) : rawCount;

    const safeRating = Number.isFinite(ratingNum) ? Math.min(5, Math.max(0, ratingNum)) : null;
    const safeCount = Number.isFinite(countNum) ? Math.max(0, countNum) : null;

    if (safeRating === null && (safeCount === null || safeCount === 0)) return null;

    return {
      rating: safeRating,
      count: safeCount,
    };
  };

  const ratingInfo = getRatingInfo();

  const CardWrapper = ({ children }) => {
    const handleCardClick = (e) => {
      // If it's a preview, we do nothing and let the parent handler take over.
      if (isPreview) {
        return;
      }
      // If it's a real card, we prevent any parent handlers and navigate.
      e.preventDefault();
      e.stopPropagation();
      navigate(`/products/${product.id}`);
    };

    if (isPreview) {
      // For the preview, we just render a div. The parent component will wrap this
      // with its own onClick handler for the file upload.
      return <div className="product-card-container">{children}</div>;
    }

    // For a real card, we use a Link for navigation and semantics.
    return (
      <Link to={`/products/${product.id}`} onClick={handleCardClick} className="product-card-container text-decoration-none">
        {children}
      </Link>
    );
  };

  return (
    <CardWrapper>
      <motion.div
        className="product-card-fresh"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -8, transition: { duration: 0.2 } }}
      >
        {/* Product Image with overlay */}
        <motion.div 
          className="product-image-wrapper"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <img 
            src={productImage} 
            alt={product.name} 
            className="product-image-fresh"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://picsum.photos/400/300?grayscale';
            }}
          />
          
          {/* Wishlist button overlay */}
          <motion.button
            className={`wishlist-overlay-btn ${inWishlist ? 'active' : ''}`}
            onClick={handleWishlistToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <motion.div
              initial={false}
              animate={{ scale: inWishlist ? [1, 1.2, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              {inWishlist ? <FaHeart /> : <FaRegHeart />}
            </motion.div>
          </motion.button>
          
          {/* Stock badge */}
          {product.stock && product.stock > 0 && (
            <motion.div
              className="stock-badge-overlay"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <FaBox className="me-1" />
              {product.stock} in stock
            </motion.div>
          )}
        </motion.div>

        {/* Product Info Section - All elements below image */}
        <motion.div 
          className="product-info-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* Product Title and Seller Type */}
          <div className="product-header">
            <h3 className="product-title-fresh">{product.name}</h3>
            {product.seller?.type && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
              >
                <Badge 
                  className={`seller-badge ${product.seller.type === 'student' ? 'student' : 'pro'}`}
                >
                  <FaUniversity className="seller-icon" />
                  {product.seller.type === 'student' ? 'Student' : 'Pro'}
                </Badge>
              </motion.div>
            )}
          </div>
          
          {/* Category and Condition Row */}
          <div className="product-meta">
            {product.category && (
              <motion.span 
                className="category-tag"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FaTag className="me-1" />
                {product.category}
              </motion.span>
            )}
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Badge className={`condition-badge-fresh ${conditionClass}`}>
                <FaCheckCircle className="condition-icon" /> {conditionLabel}
              </Badge>
            </motion.span>
          </div>
          
          {/* Optional description */}
          {getPlainTextDescription(product.description) && (
            <motion.div 
              className="product-description-fresh"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {(() => {
                const text = getPlainTextDescription(product.description);
                return text.length > 75 ? `${text.slice(0, 75)}...` : text;
              })()}
            </motion.div>
          )}

          {/* Rating */}
          {ratingInfo && (
            <motion.div
              className="rating-row-fresh"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
            >
              <div className="rating-stars-fresh" aria-label="Product rating">
                {Array.from({ length: 5 }).map((_, idx) => {
                  const starIndex = idx + 1;
                  const filled = typeof ratingInfo.rating === 'number' && ratingInfo.rating >= starIndex;
                  return (
                    <span key={starIndex} className={`rating-star-fresh ${filled ? 'filled' : ''}`}>
                      {filled ? <FaStar /> : <FaRegStar />}
                    </span>
                  );
                })}
              </div>
              <div className="rating-text-fresh">
                {typeof ratingInfo.rating === 'number' ? ratingInfo.rating.toFixed(1) : 'New'}
                {typeof ratingInfo.count === 'number' ? ` (${ratingInfo.count})` : ''}
              </div>
            </motion.div>
          )}
          
          {/* Features pills */}
          {getFeaturesArray(product.features).length > 0 && (
            <motion.div 
              className="product-features-fresh"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
            >
              {getFeaturesArray(product.features).slice(0, 2).map((feature, idx) => (
                <motion.span 
                  key={idx} 
                  className="feature-pill-fresh"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + idx * 0.05 }}
                >
                  {feature}
                </motion.span>
              ))}
            </motion.div>
          )}
          
          {/* Price */}
          <motion.div 
            className="price-section"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="price-container-fresh">
              <FaMoneyBillWave className="price-icon" />
              <span className="current-price-fresh">{formatPrice(product.price)}</span>
            </div>
          </motion.div>
          
          {/* Action Buttons */}
          <motion.div 
            className="card-actions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <motion.button
              className="cart-btn-new"
              onClick={handleAddToCart}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaShoppingCart className="me-2" />
              Add to Cart
            </motion.button>
            
            <motion.button
              className="view-btn-new"
              onClick={handleViewProduct}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaEye className="me-2" />
              View
            </motion.button>
          </motion.div>
        </motion.div>
        
        {/* Added to Cart Animation */}
        <AnimatePresence>
          {showAddedToCart && (
            <motion.div 
              className="added-to-cart-animation"
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: -20 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <FaShoppingCart className="added-icon" />
              </motion.div>
              <span>Added to Cart!</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </CardWrapper>
  );
};

export default ProductCard;
